import { combineReducers } from 'redux';
import { getFormValues, startSubmit, stopSubmit } from 'redux-form';
import axios from 'axios';
import isString from 'lodash/isString';

import { CLIENT_BONUS_FORM, CONFIRM_CLIENT_BONUS, UNCONFIRM_CLIENT_BONUS } from 'src/constants/FormNames';
import * as Urls from 'src/constants/EndpointUrls';
import { concatParamsToUrl, redirectTo } from 'src/utils/Http';
import createNamedWrapperReducer from 'src/reducers/createNamedWrapperReducer';
import { getItemErrors, setItem, setItemError } from 'src/reducers/common/itemReducer';
import { setList, getList } from 'src/reducers/common/listReducer';
import { setGlobalErrors, setGlobalSuccesses, setGlobalWarnings } from 'src/reducers/global';
import { fetchBonusOptions } from 'src/reducers/searchForm';
import { formatDate } from 'src/utils/Date';
import { fetchConfirmJobStatus } from 'src/reducers/JobStatus';
import { listReducer, itemReducer } from '../common';
import { fetchEmployeeBonus, getEmployeeBonusQueries } from '../employees/employeeBonus';

export const REDUCER_NAME = 'clientBonus';

export const getClientBonusItemErrors = state => (getItemErrors(REDUCER_NAME, state) || []).filter(isString);

export const getClientBonusFormValues = state => getFormValues(CLIENT_BONUS_FORM)(state);
export const getClientBonusList = state => getList(REDUCER_NAME, state);
export const setClientBonusList = data => setList(REDUCER_NAME, data);
export const clearErrors = () => dispatch => dispatch(setItemError(REDUCER_NAME, []));
export const setErrors = errors => dispatch => dispatch(setItemError(REDUCER_NAME, errors));
export const setChangeConfirmBonusErrors = errors => dispatch =>
  dispatch(setItemError(REDUCER_NAME, [{ changeConfirmBonusErrors: errors }]));
export const getChangeConfirmBonusErrors = state => {
  const errors = getItemErrors(REDUCER_NAME, state) || [{}];
  return (errors[0] || {}).changeConfirmBonusErrors || [];
};

// execute if callback is function
const executeCallBack = callback => {
  if (typeof callback === 'function') {
    callback();
  }
};

export const fetchBonus = id => async dispatch => {
  try {
    axios.get(concatParamsToUrl(Urls.CLIENT_BONUS_UPDATE, { id })).then(response => {
      const clientBonus = response.data.payload.clientBonus;
      const { applyForResignedFrom, applyForResignedTo } = response.data.payload.clientBonus;
      dispatch(
        setItem(REDUCER_NAME, {
          ...clientBonus,
          applyForResignedFrom: formatDate(applyForResignedFrom),
          applyForResignedTo: formatDate(applyForResignedTo)
        })
      );
    });
  } catch (exception) {
    dispatch(setErrors(exception.response.data.errors.messages));
  }
};

export const newBonus = values => async dispatch => {
  try {
    dispatch(startSubmit(CLIENT_BONUS_FORM));
    const {
      data: {
        payload: {
          clientBonus: { id }
        }
      }
    } = await axios.post(Urls.CLIENT_BONUS_CREATE, values);
    redirectTo(`${Urls.EMPLOYEE_BONUS_LIST}?client_bonus_id=${id}`);
  } catch (exception) {
    dispatch(setErrors(exception.response.data.errors.messages));
  } finally {
    dispatch(stopSubmit(CLIENT_BONUS_FORM));
  }
};

export const editBonus = (id, values) => async dispatch => {
  try {
    dispatch(startSubmit(CLIENT_BONUS_FORM));
    await axios.put(concatParamsToUrl(Urls.CLIENT_BONUS_UPDATE, { id }), values);
    window.location.reload();
  } catch (exception) {
    if (exception.response.status === 409) {
      dispatch(setGlobalErrors(exception.response.data.errors.messages));
    } else {
      dispatch(setErrors(exception.response.data.errors.messages));
    }
  } finally {
    dispatch(stopSubmit(CLIENT_BONUS_FORM));
  }
};

// @param data:
// releaseDate: 公開日
// sendNotifyToEmployee: 従業員にメールを送るかどうかのフラッグ
export const confirmClientBonus = (clientBonusId, data, onSuccessCallBack, closeModalCallBack) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(clearErrors());
    dispatch(startSubmit(CONFIRM_CLIENT_BONUS));

    const { releaseDate = '', releaseHour = '' } = data;
    const clientBonusList = getClientBonusList(getState());
    const params = { ...data, releaseDate: releaseDate.concat(' ').concat(releaseHour) };

    const confirmUrl = concatParamsToUrl(Urls.CLIENT_BONUS_CONFIRM, { id: clientBonusId });
    const response = await axios.put(confirmUrl, params);

    dispatch(stopSubmit(CONFIRM_CLIENT_BONUS));
    dispatch(
      setList(REDUCER_NAME, {
        data: [...clientBonusList.filter(item => item.id !== clientBonusId), response.data.payload.clientBonus]
      })
    );

    executeCallBack(onSuccessCallBack);
    executeCallBack(closeModalCallBack);
    dispatch(fetchConfirmJobStatus());
    // Re fetch bonus
    dispatch(fetchBonusOptions());
    dispatch(fetchEmployeeBonus(getEmployeeBonusQueries(getState())));
    dispatch(setGlobalWarnings('賞与確定を処理中です。しばらくお待ちください。'));
  } catch (exception) {
    dispatch(stopSubmit(CONFIRM_CLIENT_BONUS));
    dispatch(setChangeConfirmBonusErrors(exception.response.data.errors.messages));
  }
};

export const unconfirmClientBonus = (clientBonusId, onSuccessCallBack, closeModalCallBack) => async (
  dispatch,
  getState
) => {
  try {
    dispatch(clearErrors());
    dispatch(startSubmit(UNCONFIRM_CLIENT_BONUS));

    const clientBonusList = getClientBonusList(getState());
    const unconfirmUrl = concatParamsToUrl(Urls.CLIENT_BONUS_UNCONFIRM, { id: clientBonusId });
    const response = await axios.put(unconfirmUrl);

    dispatch(stopSubmit(UNCONFIRM_CLIENT_BONUS));
    dispatch(
      setList(REDUCER_NAME, {
        data: [
          ...clientBonusList.filter(item => item !== undefined && item.id !== clientBonusId),
          response.data.payload.clientBonus
        ]
      })
    );

    executeCallBack(onSuccessCallBack);
    executeCallBack(closeModalCallBack);
    // Re fetch bonus
    dispatch(fetchBonusOptions());
    dispatch(fetchEmployeeBonus(getEmployeeBonusQueries(getState())));
    dispatch(setGlobalSuccesses('賞与の確定を解除しました。'));
  } catch (exception) {
    dispatch(stopSubmit(UNCONFIRM_CLIENT_BONUS));
    dispatch(setChangeConfirmBonusErrors(exception.response.data.errors.messages));
  }
};

export const deleteClientBonus = clientBonusId => async dispatch => {
  try {
    const deleteUrl = concatParamsToUrl(Urls.CLIENT_BONUS_DELETE, { id: clientBonusId });
    await axios.delete(deleteUrl);

    redirectTo(Urls.EMPLOYEE_BONUS_LIST);
  } catch (exception) {
    if (exception.response.status === 409) {
      dispatch(setGlobalErrors(exception.response.data.errors.messages));
    } else {
      dispatch(setErrors(exception.response.data.errors.messages));
    }
  }
};

export const completeBonusPaymentNotification = (bonus, onSuccessCallback) => async (dispatch, getState) => {
  try {
    const url = concatParamsToUrl(Urls.COMPLETE_BONUS_PAYMENT_NOTIFICATION_URL, { id: bonus.id });
    await axios.put(url);
    const bonusList = getClientBonusList(getState());
    dispatch(setClientBonusList({ data: [...bonusList.filter(item => item.id !== bonus.id)] }));
    onSuccessCallback();
    dispatch(setGlobalSuccesses('Todoを完了しました。'));
  } catch (exception) {
    dispatch(setGlobalErrors(exception.response.data.errors.messages));
  }
};

export default combineReducers({
  list: createNamedWrapperReducer(listReducer, REDUCER_NAME),
  item: createNamedWrapperReducer(itemReducer, REDUCER_NAME)
});
